<template>
  <div
    class="fake-progress"
    :aria-valuemin="0"
    :aria-valuemax="100"
    :aria-valuenow="Math.round(progress)"
    role="progressbar"
  >
    <div class="fake-progress__track">
      <div
        class="fake-progress__bar"
        :style="{ transform: 'scaleX(' + progress / 100 + ')' }"
      ></div>
    </div>
  </div>
</template>

<script>
  export default {
    name: 'FakeLoader',

    props: {
      isAutostart: { type: Boolean, default: true },
      maxFake: { type: Number, default: 90 },
      durationToMaxFake: { type: Number, default: 100000 },
      completeDuration: { type: Number, default: 400 },
      easing: {
        type: Function,
        default: function easeOutCubic(t) {
          return 1 - Math.pow(1 - t, 3);
        },
      },
    },

    data() {
      return {
        progress: 0,
        animationStartTime: 0,
        animationFrameId: null,
        state: 'running', // 'running' | 'completing' | 'done'
      };
    },

    mounted() {
      if (this.isAutostart) this.start();
    },

    beforeDestroy() {
      this.cancelAnimationFrame();
    },

    methods: {
      start() {
        this.cancelAnimationFrame();
        this.progress = 0;
        this.animationStartTime = performance.now();
        this.state = 'running';
        this.$emit('start');
        this.tick();
      },

      complete() {
        if (this.state === 'done' || this.state === 'completing') return;

        this.state = 'completing';
        const from = this.progress;
        const to = 100;
        const duration = Math.max(0, this.completeDuration);

        const completionStartTime = performance.now();
        const animateTo100 = () => {
          const now = performance.now();
          const progressRatio = Math.min(1, (now - completionStartTime) / duration);
          const easedProgress = this.easing(progressRatio);
          const newProgress = from + (to - from) * easedProgress;

          if (Math.abs(newProgress - this.progress) > 0.01) {
            this.progress = newProgress;
            this.$emit('update', this.progress);
          }

          if (progressRatio < 1) {
            this.animationFrameId = requestAnimationFrame(animateTo100);
          } else {
            this.progress = 100;
            this.state = 'done';
            this.$emit('update', this.progress);
            this.$emit('finish');
            this.cancelAnimationFrame();
          }
        };

        this.cancelAnimationFrame();
        this.animationFrameId = requestAnimationFrame(animateTo100);
      },

      tick() {
        if (this.state !== 'running') return;

        const now = performance.now();
        const elapsed = Math.max(0, now - this.animationStartTime);
        const progressRatio = Math.min(1, elapsed / this.durationToMaxFake);

        const easedProgress = this.easing(progressRatio);
        const target = this.maxFake * easedProgress;

        if (Math.abs(target - this.progress) > 0.01) {
          this.progress = target;
          this.$emit('update', this.progress);
        }

        if (progressRatio < 1 && this.progress < this.maxFake) {
          this.animationFrameId = requestAnimationFrame(() => this.tick());
        } else {
          this.progress = this.maxFake;
          this.$emit('update', this.progress);
          this.cancelAnimationFrame();
        }
      },

      cancelAnimationFrame() {
        if (this.animationFrameId != null) {
          cancelAnimationFrame(this.animationFrameId);
          this.animationFrameId = null;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .fake-progress {
    user-select: none;
    width: 200px;

    &__track {
      width: 100%;
      height: 8px;
      background: #fff;
      border: 1px solid $color-bg-second;
      border-radius: $border-radius__md;
      overflow: hidden;
    }

    &__bar {
      height: 100%;
      width: 100%;
      background: $color-bg-primary;
      border-radius: calc(#{$border-radius__md} - 1px);
      transition: transform 16ms linear;
      will-change: transform;
      transform-origin: left center;
      transform: scaleX(0);
    }
  }

  @media (max-width: $screen-xxs-max) {
    .fake-progress {
      min-width: 160px;
    }
  }
</style>
